<template>
  <div>
    <CookieDialog
      :isCookieDialogShown="isCookieDialogShown"
      :popUpTitle="popUpTitle"
      :popUpDescription="popUpDescription"
      :essentialCookiesTexts="essentialCookiesTexts"
      :marketingCookiesTexts="marketingCookiesTexts"
      :isEssentialCookiesAccepted="isEssentialCookiesAccepted"
      :isMarketingCookiesAccepted="isMarketingCookiesAccepted"
      @toggleMarketingCookies="isMarketingCookiesAccepted = !isMarketingCookiesAccepted"
      @toggleAllUnnecessaryCookies="toggleAllUnnecessaryCookies"
      @acceptCookieConsent="acceptCookieConsent"
      @close="close"
    />
    <CookieBanner
      :bannerDescription="bannerDescription"
      :manageBtnText="manageBtnText"
      :acceptBtnText="acceptBtnText"
      :isCookieEnabled="isCookieEnabled"
      :isCookieDialogShown="isCookieDialogShown"
      @openCookieDialog="isCookieDialogShown = true"
      @toggleAllUnnecessaryCookies="toggleAllUnnecessaryCookies"
      @acceptCookieConsent="(acceptAll) => acceptCookieConsent(acceptAll)"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'CookieManagement',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { SCookie, SStrapiService, useCmsLocaleReload } from '@slg/web-customer-shared';
import CookieBanner from '@/components/cookie/CookieBanner.vue';
import CookieDialog from '@/components/cookie/CookieDialog.vue';
import {
  essentialCookieConsent,
  marketingCookieConsent,
  cookieConsentValidValue,
  cookieConsentNotValidValue,
  cookieMaxDaysExpire,
  gtmCookiesPrefixes,
  oldCookieConsent,
} from '@/utils/constants';
import { useStore, useRouter, useI18n } from '@/composables';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const isCookieEnabled = ref(true);
const isEssentialCookiesAccepted = ref(true);
const isMarketingCookiesAccepted = ref(false);
const isCookieDialogShown = ref(false);
const essentialCookiesTexts = ref({ title: '', description: '' });
const marketingCookiesTexts = ref({ title: '', description: '' });
const manageBtnText = ref('');
const acceptBtnText = ref('');
const bannerDescription = ref('');
const popUpTitle = ref('');
const popUpDescription = ref('');

const getIsShownCookieDialog = computed(() => store.getters['common/getIsShownCookieDialog']);

watch(getIsShownCookieDialog, () => {
  isCookieDialogShown.value = getIsShownCookieDialog.value;
}, { immediate: true });

watch(isCookieDialogShown, () => {
  isMarketingCookiesAccepted.value = SCookie.cookieEnabledChecker(marketingCookieConsent, cookieConsentValidValue);
}, { immediate: true });

if (SCookie.cookieEnabledChecker(oldCookieConsent, cookieConsentValidValue)) {
  SCookie.deleteCookie(oldCookieConsent);
}

async function loadCmsData() {
  try {
    const {
      manageButton,
      acceptButton,
      bannerDescription: cmsBannerDescription,
      essentialCookie,
      marketingCookie,
      popUpTitle: cmsPopUpTitle,
      popUpDescription: cmsPopUpDescription,
    } = await SStrapiService.getCookieConsent();

    manageBtnText.value = manageButton || t('cookieManagement.manageBtnText');
    acceptBtnText.value = acceptButton || t('cookieManagement.acceptButton');
    bannerDescription.value = cmsBannerDescription || t('cookieManagement.bannerDescription');
    essentialCookiesTexts.value = essentialCookie
    || {
      title: t('cookieManagement.essentialCookie.title'),
      description: t('cookieManagement.essentialCookie.description'),
    };
    marketingCookiesTexts.value = marketingCookie
    || {
      title: t('cookieManagement.marketingCookie.title'),
      description: t('cookieManagement.marketingCookie.description'),
    };
    popUpTitle.value = cmsPopUpTitle || t('cookieManagement.popUpTitle');
    popUpDescription.value = cmsPopUpDescription || t('cookieManagement.popUpDescription');
    isCookieEnabled.value = SCookie.cookieEnabledChecker(essentialCookieConsent, cookieConsentValidValue);
  } catch (e) {
    // to-do rethink about catch
  }
}

function acceptCookieConsent(acceptAll = false) {
  if (!SCookie.cookieEnabledChecker(essentialCookieConsent, cookieConsentValidValue)) {
    SCookie.deleteCookie(essentialCookieConsent);
    SCookie.setCookie(essentialCookieConsent, cookieConsentValidValue, cookieMaxDaysExpire);
  }

  if ((isMarketingCookiesAccepted.value || acceptAll)
    && !SCookie.cookieEnabledChecker(marketingCookieConsent, cookieConsentValidValue)) {
    SCookie.deleteCookie(marketingCookieConsent);
    SCookie.setCookie(marketingCookieConsent, cookieConsentValidValue, cookieMaxDaysExpire);
  } else {
    SCookie.deleteCookie(marketingCookieConsent);
    SCookie.setCookie(marketingCookieConsent, cookieConsentNotValidValue, cookieMaxDaysExpire);
    SCookie.removeDomainCookiesByPrefix(gtmCookiesPrefixes);
  }

  isCookieEnabled.value = SCookie.cookieEnabledChecker(essentialCookieConsent, cookieConsentValidValue);
  router.go();
}

function toggleAllUnnecessaryCookies() {
  isMarketingCookiesAccepted.value = false;
}

function close() {
  isCookieDialogShown.value = false;

  if (getIsShownCookieDialog.value) {
    store.commit('common/changeCookieDialogOpenState', false);
  }
}

useCmsLocaleReload(loadCmsData);
</script>
